import { gql } from "@apollo/client";
import { TrashIcon } from "@heroicons/react/outline";
import { format, parseJSON } from "date-fns";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useMount, useTitle } from "react-use";
import Button from "../../../components/Button";
import { FormField } from "../../../components/forms/shared";
import TextInput from "../../../components/forms/TextInput";
import Modal from "../../../components/Modal";
import { ModalCloseButton } from "../../../components/Modal/Modal";
import QueryComponent from "../../../components/QueryComponent";
import Spinner from "../../../components/Spinner";
import { deleteUser } from "../../../services/user";
import { logout } from "../../../store/modules/user/actions";

const Account = () => {
  useTitle("Settings - Account | Due");
  const dispatch = useDispatch();
  const [deleting, setDeleting] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");

  useMount(() => {
    document.getElementById("app-container")?.scrollTo({ top: 0 });
  });

  const confirmDeleteAccount = () => {
    setDeleting(true);
    deleteUser(deleteConfirm).subscribe(
      () => {
        setDeleting(false);
        toast("Account has been deleted.");
        dispatch(logout());
      },
      () => {
        setDeleting(false);
        toast("Account failed to be deleted. Please contact customer support.");
      }
    );
  };

  return (
    <>
      <div className="py-6 px-4 sm:p-6 lg:pb-8 w-full">
        <h2 className="text-lg leading-6 font-medium text-gray-900">Account</h2>
        <p className="mt-1 text-sm text-gray-500 mb-6">
          Manage how information is displayed on your account.
        </p>
        <QueryComponent
          query={{
            query: gql`
              {
                self {
                  uuid
                  email
                  fullName
                  dateJoined
                }
              }
            `,
          }}
          options={{ onLoadRefetch: true }}
          loader={() => (
            <div className="w-full h-80 flex items-center justify-center">
              <Spinner />
            </div>
          )}
          error={() => (
            <div className="h-80 flex items-center justify-center">
              <div className="text-red-500">
                Failed to load user data. Please try again later.
              </div>
            </div>
          )}
        >
          {(data) => {
            const {
              self: { fullName, email, dateJoined },
            } = data;
            return (
              <dl className="sm:divide-y sm:divide-gray-200">
                <InfoSection label="Full name" value={fullName} />
                <InfoSection label="Email" value={email} />
                <InfoSection
                  label="Date joined"
                  value={format(parseJSON(dateJoined), "MMM d, yyyy h:mm a")}
                />
              </dl>
            );
          }}
        </QueryComponent>
      </div>
      <div className="p-6 mb-16">
        <button
          className="inline-flex items-center text-sm text-red-500 hover:text-red-700"
          onClick={() => setDeleteModal(true)}
        >
          <TrashIcon className="w-6 h-6 mr-2" />
          <div className="flex-shrink-0">Delete Account</div>
        </button>
      </div>

      {deleteModal && (
        <Modal
          isOpen
          onRequestClose={() => {
            setDeleteModal(false);
          }}
        >
          <div className="p-8 relative" style={{ width: 600 }}>
            <ModalCloseButton onRequestClose={() => setDeleteModal(false)} />
            <h2 className="text-2xl font-semibold mb-2">Delete Due Account</h2>
            <p className="mb-4">
              Are you sure you want to delete your Due account? This action is
              permanent and you won't be able to undo it.
            </p>
            <FormField
              label={
                <p>
                  Type <b>"delete due"</b> to confirm deleting account.
                </p>
              }
            >
              <TextInput
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
              />
            </FormField>
            <div className="flex mt-4">
              <Button
                loading={deleting}
                color="danger"
                disabled={deleteConfirm !== "delete due"}
                onClick={() => {
                  confirmDeleteAccount();
                }}
              >
                Delete Account
              </Button>
              <Button
                disabled={deleting}
                color="transparent"
                className="ml-4"
                onClick={() => {
                  setDeleteModal(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

const InfoSection = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
        {value}
      </dd>
    </div>
  );
};

export default Account;
